/**
 * PartnerThemeDemo — proves the white-label theming machinery end-to-end.
 *
 * Renders the locked component grammar (SectionHeader, Callouts, In-This-
 * Blueprint card, Friction/Counter-Move pair, Part-of-Tens, Wordmark) inside
 * a runtime theme-switcher. Picking a partner brand swaps the entire surface
 * via CSS-variable injection — the foundation tokens (typography, motion,
 * spacing, radius, semantic mappings, callout structure) stay locked per
 * ADR-003 / tokens v2 override-surface lock.
 *
 * Per Design Brief §11 Phase 4 deliverable: "3 sample partner brands rendered
 * through the system to verify themability."
 */

import { useState, useEffect } from "react";
import {
  Wordmark, SectionHeader, Callout, InThisBlueprint, FrictionCounterMove,
  PartOfTens, Chip, NumberedList, CheckList, HedgeMark, AuditFooter,
} from "../components/index.js";
import { SAMPLE_THEMES, SAMPLE_THEME_LABELS } from "../lib/tokens/sample-partner-themes.js";

const THEMES = [
  { id: "default",     label: "PeoplePrints (default)" },
  { id: "consultancyA", label: SAMPLE_THEME_LABELS.consultancyA },
  { id: "consultancyB", label: SAMPLE_THEME_LABELS.consultancyB },
  { id: "consultancyC", label: SAMPLE_THEME_LABELS.consultancyC },
];

export default function PartnerThemeDemo() {
  const [activeId, setActiveId] = useState("default");

  // Apply the theme to a scoped wrapper. Since the locked v2 token system
  // exposes accent.* via CSS custom properties on :root, we shadow them on a
  // scoped wrapper here so the rest of the app stays default-themed.
  const overrides = activeId === "default" ? {} : (SAMPLE_THEMES[activeId] || {});

  // Map "color.accent.navy" → "--ppr-color-accent-navy"
  const cssVarOverrides = Object.fromEntries(
    Object.entries(overrides).map(([path, val]) => [
      "--ppr-" + path.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase().replace(/\./g, "-"),
      val,
    ])
  );

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 24px" }}>
      <Wordmark scale={1.0} />

      <SectionHeader index={1} title="White-label theming demo" />

      <Callout.Tip>
        Pick a partner brand below. The component grammar stays identical; the only thing that changes is the runtime CSS-variable values for <code>color.accent.*</code> and brand asset URLs. Foundation tokens (typography, motion, spacing, radius, semantic mappings, callout structure) stay tenant-locked per ADR-003.
      </Callout.Tip>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "20px 0" }}>
        {THEMES.map((t) => (
          <Chip key={t.id} primary={activeId === t.id} onClick={() => setActiveId(t.id)}>
            {t.label}
          </Chip>
        ))}
      </div>

      {/* The themed wrapper. CSS variables shadow only inside this div. */}
      <div style={cssVarOverrides} data-component="ThemedShowcase">
        <SectionHeader index={2} title="Demonstrator surface — re-skinned live" />

        <InThisBlueprint
          items={[
            <span key="1"><strong>Reading the room</strong> — what tends to land</span>,
            <span key="2">The opening that <HedgeMark>probably</HedgeMark> disarms</span>,
            <span key="3">Where things go sideways and how to bring them back</span>,
          ]}
        />

        <Callout.Tip>
          Brand color is the one variable that flips. <strong>Notice the chip border, the section badge, the callout label, the In-This-Blueprint top stripe, and the Wordmark all re-skin together.</strong>
        </Callout.Tip>

        <Callout.WatchOut>
          Don't open with "How are you doing?" — it <HedgeMark>tends to</HedgeMark> read as concealed evaluation.
        </Callout.WatchOut>

        <Callout.Remember>
          Warmth doesn't disarm Marcus. Structure does.
        </Callout.Remember>

        <Callout.KeyInsight>
          The whole conversation pivots on whether Marcus believes you're there to <strong>work with him</strong> or <strong>evaluate him</strong>.
        </Callout.KeyInsight>

        <FrictionCounterMove
          friction={<>Marcus <HedgeMark>tends to</HedgeMark> interpret "how are you doing?" as the opening of a performance review. <strong>Watch for him bracing.</strong></>}
          counterMove={<>Open with a <strong>frame</strong>, not a question: "I want this to be a working conversation, not an evaluation." Then ask the specific question.</>}
        />

        <PartOfTens
          items={[
            <span key="1">Marcus opens quiet — <strong>wait through the silence.</strong></span>,
            <span key="2"><strong>Don't lead with warmth</strong> — it reads as preamble to bad news.</span>,
            <span key="3">Lead with <strong>structure</strong>: name the meeting, name what you noticed.</span>,
          ]}
        />

        <CheckList
          items={[
            <span key="1">If he goes quiet mid-meeting, <strong>ask "what would help right now?"</strong></span>,
            <span key="2"><strong>Don't summarize his answers</strong> back to him.</span>,
          ]}
        />

        <NumberedList
          items={[
            <span key="1">First, name the meeting.</span>,
            <span key="2">Second, name what you noticed.</span>,
            <span key="3">Third, ask one specific question.</span>,
          ]}
        />

        <AuditFooter
          engineVersion="elemental-influence@1.0.0+2026-05-02"
          jargonScanPassed={true}
          consentRecord="marcus.liang@ · 2026-04-12"
          blueprintId="DEMO-2026-0502-00001"
          pageIndicator="page 1 of 1"
        />
      </div>

      <SectionHeader index={3} title="What just changed" />

      <p style={{
        fontFamily: "var(--ppr-typography-font-family-body)",
        fontSize: "var(--ppr-typography-font-size-base)",
        lineHeight: "var(--ppr-typography-line-height-relaxed)",
        color: "var(--ppr-color-text-secondary)",
      }}>
        Active overrides for <strong>{THEMES.find((t) => t.id === activeId).label}</strong>:
      </p>

      <pre style={{
        fontFamily: "var(--ppr-typography-font-family-mono)",
        fontSize: "11px",
        background: "var(--ppr-color-background-sub)",
        padding: "12px 14px",
        border: "1px solid var(--ppr-color-border-default)",
        overflow: "auto",
        color: "var(--ppr-color-text-secondary)",
      }}>
        {Object.keys(overrides).length === 0
          ? "(no overrides — default PeoplePrints theme)"
          : JSON.stringify(overrides, null, 2)}
      </pre>
    </div>
  );
}
